// ============================================================================
// THEME STATE — light · dark · system.
//
// The handoff designs one appearance; dark mode is an owner request on top of
// it (2026-08-26). Everything visual lives in the `[data-theme='dark']` block at
// the end of src/index.css. This file owns exactly three things:
//
//   1. WHAT THE OWNER CHOSE       — 'light' | 'dark' | 'system', in localStorage.
//   2. WHAT THAT RESOLVES TO NOW  — 'light' | 'dark', after asking the OS.
//   3. STAMPING IT ON <html>      — `data-theme` carries the RESOLVED value and
//      nothing else, so the stylesheet needs one selector and never a
//      `prefers-color-scheme` query. `data-theme-choice` carries the choice, for
//      the control's own highlighted state and for anyone reading the DOM.
//
// The functions are pure and take their world as arguments (storage, document,
// a boolean for the OS) because that is what makes the resolution rules
// testable without a browser — see theme.test.ts.
// ============================================================================

export type ThemeChoice = 'light' | 'dark' | 'system'
export type ResolvedTheme = 'light' | 'dark'

export const THEME_CHOICES: readonly ThemeChoice[] = ['light', 'dark', 'system']

/** The localStorage key. Namespaced, because a personal OS will grow more of these. */
export const THEME_STORAGE_KEY = 'lifeos.theme'

/** The OS-level query. One string, used by the hook and by the pre-paint script alike. */
export const DARK_QUERY = '(prefers-color-scheme: dark)'

/** The default when nothing has ever been chosen: follow the machine. */
export const DEFAULT_CHOICE: ThemeChoice = 'system'

function isChoice(value: unknown): value is ThemeChoice {
  return value === 'light' || value === 'dark' || value === 'system'
}

/**
 * What the owner last chose, or the default.
 *
 * A corrupt or stale value ('auto', '"dark"', anything a past version wrote) is
 * treated as "never chose", not as an error: the worst outcome of a bad read is
 * the app opening in the wrong theme, which is not worth a thrown exception on
 * the render path. `localStorage` itself throws in a locked-down browser
 * profile, so the read is guarded too.
 */
export function readChoice(storage: Pick<Storage, 'getItem'> | null | undefined): ThemeChoice {
  try {
    const raw = storage?.getItem(THEME_STORAGE_KEY)
    return isChoice(raw) ? raw : DEFAULT_CHOICE
  } catch {
    return DEFAULT_CHOICE
  }
}

/**
 * Persist the choice. 'system' is written out rather than cleared — "I want to
 * follow the machine" and "I have never said" are the same rendering today and
 * a different sentence, and the control has to be able to show which one it is.
 */
export function writeChoice(
  storage: Pick<Storage, 'setItem'> | null | undefined,
  choice: ThemeChoice,
): void {
  try {
    storage?.setItem(THEME_STORAGE_KEY, choice)
  } catch {
    // A browser that refuses storage still gets a working toggle for this
    // session. Silently losing the preference beats refusing to switch.
  }
}

/** The one resolution rule, in one place. */
export function resolveTheme(choice: ThemeChoice, systemPrefersDark: boolean): ResolvedTheme {
  if (choice === 'light') return 'light'
  if (choice === 'dark') return 'dark'
  return systemPrefersDark ? 'dark' : 'light'
}

/**
 * Stamp the document. Also updates `<meta name="theme-color">`, which is what
 * Android paints the status bar and the installed-PWA title bar with — a white
 * bar above a near-black app is the tell that a dark theme was bolted on.
 *
 * The colour is READ BACK OFF THE PAGE rather than written here: `--color-page`
 * is already the token that decides it, and the ship gate forbids a hex literal
 * outside index.css precisely so this kind of second source cannot appear.
 */
export function applyTheme(
  doc: Document | null | undefined,
  choice: ThemeChoice,
  resolved: ResolvedTheme,
): void {
  const root = doc?.documentElement
  if (!root) return
  root.setAttribute('data-theme', resolved)
  root.setAttribute('data-theme-choice', choice)

  const meta = doc?.querySelector('meta[name="theme-color"]')
  if (!meta) return
  const page = getComputedStyle(root).getPropertyValue('--color-page').trim()
  if (page) meta.setAttribute('content', page)
}
